export type InvitationStatus = 'PENDING' | 'SENT' | 'ACCEPTED' | 'FAILED' | 'EXPIRED';

export type InvitationChannel = 'TELEGRAM' | 'EMAIL';

export type InvitationItemTargetType =
  | 'SUBSCRIPTION_PLAN'
  | 'COURSE'
  | 'CHANNEL_PASS'
  | 'BOOK';

/**
 * Полиморфный item приглашения — продукт, который будет выдан после регистрации.
 * Возвращается с join'ом pricingTier (для отображения цены/длительности).
 */
export interface InvitationItemPayload {
  id: number;
  invitationId: number;
  targetType: InvitationItemTargetType;
  targetId: number;
  /** Локализованное название сущности (бэк резолвит для hover-карточки в списке). */
  targetTitle?: string | null;
  pricingTierId: number | null;
  /**
   * Полная форма тарифа — совместима с PricingTier из PricingTiersTooltip,
   * чтобы переиспользовать PricingTierBadge без приведений типов.
   */
  pricingTier?: import('@/components/shared/PricingTiersTooltip').PricingTier | null;
  createdAt: string;
}

export interface UserInvitation {
  id: number;
  channel: InvitationChannel;
  telegramUserId: number | null;
  telegramUsername: string | null;
  telegramFirstName: string | null;
  telegramLastName: string | null;
  telegramPhone: string | null;
  email: string | null;
  userId: number | null;
  avatarFileId: number | null;
  avatarFile?: {
    id: number;
    path: string;
  } | null;
  templateId: number | null;
  template?: InvitationTemplate | null;
  sentMessage: string | null;
  status: InvitationStatus;
  errorMessage: string | null;
  invitedByAdminId: number;
  invitedByAdmin?: {
    id: number;
    email: string;
    name: string | null;
  } | null;
  subscriptionPlanId: number | null;
  pricingTierId: number | null;
  subscriptionPlan?: {
    id: number;
    code: string;
  } | null;
  pricingTier?: {
    id: number;
    channelPassId?: number | null;
    courseId?: number | null;
    subscriptionPlanId?: number | null;
    bookId?: number | null;
    prices: Record<string, number>;
    baseCurrency: string;
    promoPrices?: Record<string, number> | null;
    durationDays: number | null;
    isActive: boolean;
    isFamily: boolean;
    familySlots: number;
    order?: number;
    targetLanguages?: string[];
  } | null;
  inviteToken: string;
  /** Готовая ссылка-приглашение на платформу (формируется бэкендом). */
  inviteUrl?: string;
  /**
   * Полиморфный список выдаваемых доступов. Возвращается всегда; пустой массив
   * означает «голое» приглашение без продуктовых доступов. Для legacy-записей
   * (созданных до 0093) — пустой массив + заполненные subscriptionPlanId.
   */
  items?: InvitationItemPayload[];
  sentAt: string | null;
  acceptedAt: string | null;
  expiresAt: string | null;
  /** До какого числа действует выдаваемый доступ; null = бессрочно / по сроку тарифа. */
  accessEndsAt: string | null;
  /** Email-tracking: первое и последнее открытие письма-приглашения (только EMAIL-канал). */
  emailFirstOpenedAt: string | null;
  emailLastOpenedAt: string | null;
  /** Email-tracking: суммарное число hit'ов пикселя (≥1 = считаем письмо открытым). */
  emailOpenCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface InvitationTemplate {
  id: number;
  name: string;
  slug: string;
  bodyTemplate: string;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TelegramUserSearchResult {
  found: boolean;
  user: {
    telegramId: number;
    username: string | null;
    firstName: string | null;
    lastName: string | null;
    phone: string | null;
    photoBase64: string | null;
    alreadyInSystem: boolean;
    existingUserId: number | null;
    alreadyInvited: boolean;
    existingInvitationId: number | null;
  } | null;
}

// Единый источник — apps/admin/src/types.ts (используется и в orders).
export type { TelegramSessionOption } from '@/types';

export interface ImportRow {
  phone?: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  subscriptionPlanCode?: string;
  /** Коды курсов через запятую (slug или id). */
  courseCodes?: string;
  /** ID channel-pass через запятую. */
  channelPassCodes?: string;
  /** Slug книг через запятую. */
  bookSlugs?: string;
}

export interface ValidatedImportRow extends ImportRow {
  telegramId?: number;
  subscriptionPlanId?: number | null;
  pricingTierId?: number | null;
  subscriptionPlanName?: string | null;
  pricingTierName?: string | null;
  items?: Array<{
    targetType: InvitationItemTargetType;
    targetId: number;
    pricingTierId: number | null;
  }>;
  resolvedItemLabels?: string[];
  status: 'found' | 'not_found' | 'already_exists' | 'already_invited' | 'error';
  error?: string;
}

export interface BulkSendResult {
  successCount: number;
  failedCount: number;
  results: Array<{
    invitationId: number;
    success: boolean;
    error?: string;
  }>;
}

export const INVITATION_STATUS_LABELS: Record<InvitationStatus, string> = {
  PENDING: 'Ожидает отправки',
  SENT: 'Отправлено',
  ACCEPTED: 'Принято',
  FAILED: 'Ошибка',
  EXPIRED: 'Истекло',
};

export const INVITATION_STATUS_COLORS: Record<InvitationStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  PENDING: 'secondary',
  SENT: 'default',
  ACCEPTED: 'default',
  FAILED: 'destructive',
  EXPIRED: 'outline',
};

export const INVITATION_CHANNEL_LABELS: Record<InvitationChannel, string> = {
  TELEGRAM: 'Telegram',
  EMAIL: 'Email',
};
